// صفحات عمومی اپ کارکنان (AP-IAM-02): پذیرش دعوت، تعیین رمز مالک و بازیابی رمز بدون نشست مستأجر.
// کلاینت عمومی فقط اگر کاربر وارد Keycloak شده باشد توکن Keycloak را می‌فرستد (پذیرش با حساب موجود — AC-IAM-012.2).
import { createPhoenixClient, type PhoenixClient } from '@phoenix/api-client';
import { acceptLanguage, useTranslation } from '@phoenix/i18n';
import { LoadingState } from '@phoenix/ui';
import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { useAuth } from './AuthProvider';

const PublicApiContext = createContext<PhoenixClient | null>(null);

export interface PublicApiProviderProps {
  baseUrl?: string;
  fetch?: typeof globalThis.fetch;
  children: ReactNode;
}

export function PublicApiProvider({ baseUrl = '', fetch: customFetch, children }: PublicApiProviderProps) {
  const auth = useAuth();
  const { i18n } = useTranslation();
  const statusRef = useRef(auth.status);
  statusRef.current = auth.status;
  const getTokenRef = useRef(auth.getAccessToken);
  getTokenRef.current = auth.getAccessToken;
  const languageRef = useRef(i18n.language);
  languageRef.current = i18n.language;

  const client = useMemo(
    () =>
      createPhoenixClient({
        baseUrl,
        fetch: customFetch,
        getLanguage: () => acceptLanguage(languageRef.current),
        getToken: async (options) =>
          statusRef.current === 'authenticated' ? getTokenRef.current(options) : undefined,
      }),
    [baseUrl, customFetch],
  );
  return <PublicApiContext.Provider value={client}>{children}</PublicApiContext.Provider>;
}

/** کلاینت API عمومی (بدون توکن مستأجر؛ با توکن Keycloak اگر کاربر وارد شده است). */
export function usePublicApi(): PhoenixClient {
  const client = useContext(PublicApiContext);
  if (!client) {
    throw new Error('usePublicApi must be used inside <PublicApiProvider>.');
  }
  return client;
}

/** Routeهای غیرعمومی: کاربر بی‌نشست (رسیده از مسیر عمومی) به ورود Keycloak هدایت می‌شود. */
export function RequireAuthenticated({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const location = useLocation();
  const anonymous = auth.status === 'anonymous';
  const { login } = auth;
  useEffect(() => {
    if (anonymous) {
      void login(`${location.pathname}${location.search}`);
    }
  }, [anonymous, login, location.pathname, location.search]);
  return anonymous ? <LoadingState /> : <>{children}</>;
}
